import { Check, ChevronDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Command, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { FIELD_LABEL_CLASS, FIELD_PLACEHOLDER_CLASS, FacetCount, FilterLabel } from './FilterControls';
import type { RangeDefinition } from './searchConstants';

export function FilterRangeInput({
  label,
  open,
  setOpen,
  selected,
  setSelected,
  ranges,
  getLabel,
  toggle,
  placeholder,
  counts,
  lock = null,
}: {
  label: string;
  open: boolean;
  setOpen: (op: boolean) => void;
  selected: string[];
  setSelected: (val: string[]) => void;
  ranges: RangeDefinition[];
  getLabel: (id: string, r: RangeDefinition[]) => string;
  toggle: (id: string) => void;
  placeholder?: string;
  /** bucket id -> company count; absent entries render no count. */
  counts?: Record<string, number>;
  /** Tier this control needs, when the visitor lacks it. */
  lock?: 'user' | 'pro' | null;
}) {
  const removeItem = (id: string) => {
    setSelected(selected.filter((x) => x !== id));
  };

  return (
    <div className="space-y-1.5">
      <FilterLabel lock={lock} className={FIELD_LABEL_CLASS}>
        {label}
      </FilterLabel>
      <Popover open={lock ? false : open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            disabled={!!lock}
            aria-expanded={open}
            className="w-full min-h-11 lg:min-h-9 h-auto font-normal text-left justify-start py-1.5 px-3 rounded-lg relative"
          >
            <div className="flex flex-wrap gap-1 w-full">
              {selected.length === 0 ? (
                <span
                  className={FIELD_PLACEHOLDER_CLASS}
                  suppressHydrationWarning
                >
                  {placeholder ?? `e.g. ${getLabel(ranges[1].id, ranges)}`}
                </span>
              ) : (
                selected.map((id) => (
                  <Badge
                    key={id}
                    variant="secondary"
                    className="text-xs font-normal gap-1 pr-1 border border-foreground"
                  >
                    <span className="max-w-[220px] truncate">
                      {getLabel(id, ranges)}
                    </span>
                    <X
                      size={12}
                      className="cursor-pointer hover:bg-muted rounded-sm"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        removeItem(id);
                      }}
                    />
                  </Badge>
                ))
              )}
              <ChevronDown className="absolute right-2 top-2 h-4 w-4 shrink-0 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0"
          align="start"
        >
          <Command>
            <CommandList>
              <CommandGroup>
                {ranges.map((range) => {
                  // A global count of 0 means no company anywhere carries
                  // this band, so selecting it can only ever return nothing.
                  // Only the bucketed facets can be 0 — the others are
                  // GROUP BY-derived and simply have no row when empty.
                  // Never disable an already-selected band, or the user
                  // cannot clear it.
                  const count = counts?.[range.id];
                  const isEmpty = count === 0 && !selected.includes(range.id);
                  return (
                    <CommandItem
                      key={range.id}
                      value={getLabel(range.id, ranges)}
                      disabled={isEmpty}
                      onSelect={() => {
                        if (!isEmpty) toggle(range.id);
                      }}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          selected.includes(range.id)
                            ? 'opacity-100'
                            : 'opacity-0',
                        )}
                      />
                      <span className={cn(isEmpty && 'text-muted-foreground/50')}>
                        {getLabel(range.id, ranges)}
                      </span>
                      <FacetCount count={count} />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
